import { useState, useEffect } from "react";
import VpsUploadField from "@/components/base/VpsUploadField";

export type ClientTemplate = {
  id: string;
  tenant_id: string;
  client_id: string;
  portal_token: string | null;
  name: string;
  template_url: string;
  preview_url: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // joined
  client_name?: string;
};

export type SimpleClient = { id: string; full_name: string; email: string };

interface Props {
  template: ClientTemplate | null;
  isNew: boolean;
  clients: SimpleClient[];
  tenantId: string;
  onClose: () => void;
  onSave: (data: Partial<ClientTemplate>) => void;
}

const BLANK: Partial<ClientTemplate> = {
  client_id: "",
  name: "",
  template_url: "",
  preview_url: "",
  notes: "",
  is_active: true,
  portal_token: "",
};

export default function ClientTemplateModal({ template, isNew, clients, tenantId, onClose, onSave }: Props) {
  const [form, setForm] = useState<Partial<ClientTemplate>>(BLANK);

  useEffect(() => {
    setForm(isNew ? BLANK : { ...template });
  }, [template, isNew]);

  const set = <K extends keyof ClientTemplate>(key: K, val: ClientTemplate[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const isValid =
    (form.client_id ?? "").length > 0 &&
    (form.name ?? "").trim().length > 0 &&
    (form.template_url ?? "").trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h2 className="text-[#0f172a] font-semibold text-base">
              {isNew ? "Asignar Template a Cliente" : "Editar Template de Cliente"}
            </h2>
            <p className="text-slate-400 text-xs mt-0.5">Template personalizado exclusivo</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-lg transition-colors cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Client */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
              Cliente <span className="text-red-400">*</span>
            </label>
            <select
              value={form.client_id ?? ""}
              onChange={(e) => set("client_id", e.target.value)}
              disabled={!isNew}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Seleccionar cliente…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.full_name}</option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
              Nombre del Template <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.name ?? ""}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Template Exclusivo Ana & Carlos"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          {/* Template Upload */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
              Template (imagen de fondo) <span className="text-red-400">*</span>
            </label>
            <VpsUploadField
              value={form.template_url ?? null}
              onChange={(url) => set("template_url", url ?? "")}
              tenantId={tenantId}
              entityType="client_invitation_template"
              entityId={template?.id}
              accept="image/*"
              showPreview
              previewHeight="h-28"
              helperText="Imagen de fondo personalizada para este cliente."
              folder="invitations"
              maxSizeMB={10}
            />
          </div>

          {/* Preview Upload */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Miniatura (opcional)</label>
            <VpsUploadField
              value={form.preview_url ?? null}
              onChange={(url) => set("preview_url", url)}
              tenantId={tenantId}
              entityType="client_invitation_template"
              entityId={template?.id}
              accept="image/*"
              showPreview
              previewHeight="h-28"
              helperText="Miniatura para el catálogo."
              folder="invitations"
              maxSizeMB={10}
            />
          </div>

          {/* Portal token */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Portal Token</label>
            <input
              type="text"
              value={form.portal_token ?? ""}
              onChange={(e) => set("portal_token", e.target.value || null)}
              placeholder="Token de acceso del cliente (opcional)"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors font-mono text-xs"
            />
            <p className="text-slate-400 text-xs mt-1">Si se completa, el cliente puede ver este template desde su portal.</p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Notas Internas</label>
            <textarea
              value={form.notes ?? ""}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
              placeholder="Notas sobre este template personalizado…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors resize-none"
            />
          </div>

          {/* Active */}
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => set("is_active", !form.is_active)}
              className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${form.is_active ? "bg-emerald-500" : "bg-slate-200"}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_active ? "translate-x-4" : "translate-x-0.5"}`} />
            </div>
            <span className="text-sm text-slate-600">Template activo</span>
          </label>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 shrink-0 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            Cancelar
          </button>
          <button
            onClick={() => isValid && onSave(form)}
            disabled={!isValid}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line"></i></span>
            {isNew ? "Asignar Template" : "Guardar Cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}